import type { ReactNode, ReactElement } from 'react';

import { useMemo, useRef, useState } from 'react';
// material
import {
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Button,
} from '@mui/material';
// icons
import MoreVert from '@mui/icons-material/MoreVert';

export interface MoreButtonProps {
  className?: string;
  tooltipTitle?: string;
  options?: any[];
  [key: string]: any;
}

// ----------------------------------------------------------------------

export default function MoreButton({
  className,
  options: allOptions,
  displayButtonForSingleOption = true,
  Icon = MoreVert,
  title,
  tooltipTitle: _tooltipTitle,
  ...other
}) {
  const ref = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  // Filter out hidden options using `showIf` which defaults to true, or
  // otherwise may be any falsey/truthy value.
  const options = useMemo(
    () => allOptions.filter(({ showIf = true }) => showIf),
    [allOptions]
  );

  // Collapse the menu if for 0 and 1 options
  if (options.length === 0) return null;

  if (options.length === 1 && displayButtonForSingleOption) {
    const [item] = options;

    return (
      <Button startIcon={item.icon} onClick={item.action} variant="text">
        {item.text}
      </Button>
    );
  }

  return (
    <>
      {title ? (
        <Button
          ref={ref}
          className={className}
          onClick={() => setIsOpen(true)}
          {...other}
          endIcon={<Icon />}
        >
          {title}
        </Button>
      ) : (
        <IconButton
          ref={ref}
          className={className}
          onClick={() => setIsOpen(true)}
          {...other}
        >
          <Icon />
        </IconButton>
      )}

      <Menu
        open={isOpen}
        anchorEl={ref.current}
        onClose={() => setIsOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {options.map(({ showIf = true, disabled = false, ...item }) =>
          showIf ? (
            <MenuItem
              key={item.text}
              data-tour={item.dataTour}
              disabled={disabled}
              onClick={(...args) => {
                if (!disabled) {
                  item.action(...args);
                  setIsOpen(false);
                }
              }}
              sx={{ color: 'text.secondary' }}
            >
              {item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </MenuItem>
          ) : null
        )}
      </Menu>
    </>
  );
}
